import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import remove from 'lodash/remove';
import set from 'lodash/set';
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

export type OrderListSerialNumber = {
  issue_date: string;
  site_code: string;
  site_country_code: string;
  site_name: string;
  value: string;
};

export type OrderListProduct = {
  customer_item_code?: string;
  description?: string;
  id: string;
  item_code?: string;
  name: string;
  product_id: string;
  qr_code?: string;
  serial_number: OrderListSerialNumber[];
};

export type OrderList = {
  approved_at?: boolean;
  created_at: string;
  customer_id: string;
  customer_name?: string;
  date: string;
  id: string;
  products: OrderListProduct[];
  short_name: string;
  tracking_url?: string;
};

type OrdersListState = {
  apiErrors: ApiErrors;
  isSearch?: boolean;
  /* list: {
    [key: string]: OrderList;
  }; */
  list: any[];
  loaders: Loaders;
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm: string;
};

export interface UpdateOrdersListPayload {
  isSearch: boolean;
  ordersLists: OrderList[];
  pagination: {
    index: number;
    indexesCount: number;
    size: number;
  };
  searchTerm: string;
}

export interface ReopenOrderListPayload {
  currentIndex: number;
  customerId: string;
  id: string;
}

const orderListsApiPrefix = '/orderlists';

// TODO: use object instead of  array
const initialState: OrdersListState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  isSearch: false,
  list: [],
  loaders: {
    isLoading: true,
    isSaving: false,
    isDeletingItems: {}
  },
  pagination: {
    size: 30,
    index: 0,
    indexesCount: 0
  },
  searchTerm: ''
};

export const fetchOrdersList = createAsyncThunk<UpdateOrdersListPayload, any, { rejectValue: string }>(
  'ordersList/fetchOrdersList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${orderListsApiPrefix}`, {
        params
      });
      const {
        data: { order_lists, size, index, indexes_count }
      } = response;
      /* let isSearch = false;
       if (get(params, 'search')) {
        isSearch = true;
      } */

      return {
        ordersLists: order_lists,
        isSearch: false,
        pagination: { size, index, indexesCount: indexes_count },
        searchTerm: ''
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteOrderList = createAsyncThunk<string, string, { rejectValue: string }>(
  'ordersList/deleteOrderList',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${orderListsApiPrefix}/${id}`);
      return id;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('ordersList.you cannot delete this user'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('ordersList.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id, value: false }));
    }
  }
);

export const reopenOrderList = createAsyncThunk<OrderList, ReopenOrderListPayload, { rejectValue: string }>(
  'ordersList/reopenOrderList',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${orderListsApiPrefix}/${payload.id}/reopen`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('ordersList.you cannot delete this order list'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('ordersList.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    }
  }
);

type OrderListSave = {
  id?: string;
  orderList: {
    customer_id: string;
    serial_numbers: string[];
    short_name?: string;
  };
};

export const saveOrderList = createAsyncThunk<OrderList, OrderListSave, { rejectValue: string }>(
  'ordersList/deleteOrderList',
  async ({ id, orderList }, { dispatch, rejectWithValue }) => {
    try {
      let response;
      if (id) {
        response = await axiosInstance.put(`${orderListsApiPrefix}/${id}`, orderList);
      } else {
        response = await axiosInstance.post(orderListsApiPrefix, orderList);
      }
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('orderList.you cannot update this order list'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('orderList.cannot save order list'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('orderList.not found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const ordersListSlice = createSlice({
  initialState,
  name: 'ordersList',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetOrdersList: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchOrdersList.fulfilled, (state, { payload }) => {
        const { ordersLists, pagination, isSearch, searchTerm } = payload;
        state.isSearch = isSearch;
        state.pagination = pagination;
        state.searchTerm = searchTerm;
        state.loaders.isLoading = false;
        state.list = [...state.list, ...ordersLists];
      })
      .addCase(fetchOrdersList.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchOrdersList.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteOrderList.fulfilled, (state, { payload }) => {
        remove(state.list, (e: OrderListProduct) => e.id === payload);
      })
      .addCase(reopenOrderList.fulfilled, (state, { payload }) => {
        state.list = state.list.map((ol: OrderList) => (ol.id === payload.id ? payload : ol));
      })
});

const { actions, reducer } = ordersListSlice;
export { reducer as ordersList };
export const { closeError, resetOrdersList, updateListDelete } = actions;

export const getOrdersList = (state: RootState) => state.ordersList.list;
export const getOrdersListLoaders = (state: RootState) => state.ordersList.loaders;
export const getOrdersListApiErrors = (state: RootState) => state.ordersList.apiErrors;
export const getOrdersListPagination = (state: RootState) => state.ordersList.pagination;
